import { type FilterOption } from '@/types';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

interface CategoryTreeNode extends FilterOption {
    children: CategoryTreeNode[];
}

interface CategoryTreeProps {
    categories: FilterOption[];
    selectedCategories: string[];
    onCategoryChange: (categoryId: string, checked: boolean) => void;
}

export const CategoryTree: React.FC<CategoryTreeProps> = ({ categories, selectedCategories, onCategoryChange }) => {
    // Build tree structure from flat list
    const categoryTree = useMemo(() => {
        const categoryMap = new Map<number, CategoryTreeNode>();
        const rootCategories: CategoryTreeNode[] = [];

        // First pass: create all nodes
        categories.forEach((category) => {
            categoryMap.set(category.id, {
                ...category,
                children: [],
            });
        });

        // Second pass: build tree structure
        categories.forEach((category) => {
            const node = categoryMap.get(category.id);
            if (!node) return;

            if (category.parent_id === null || category.parent_id === undefined) {
                rootCategories.push(node);
            } else {
                const parent = categoryMap.get(category.parent_id);
                if (parent) {
                    parent.children.push(node);
                } else {
                    rootCategories.push(node);
                }
            }
        });

        return rootCategories;
    }, [categories]);

    const hasAnySelectedDescendant = (node: CategoryTreeNode): boolean =>
        node.children.some((c) => selectedCategories.includes(c.id.toString()) || hasAnySelectedDescendant(c));

    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

    // Auto-expand ancestors of any selected category whenever selection or tree changes
    useEffect(() => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            const expandAncestors = (nodes: CategoryTreeNode[]) => {
                for (const node of nodes) {
                    if (hasAnySelectedDescendant(node)) next.add(node.id);
                    expandAncestors(node.children);
                }
            };
            expandAncestors(categoryTree);
            return next;
        });
    }, [categoryTree, selectedCategories]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleNode = (categoryId: number) => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const renderNode = (node: CategoryTreeNode, level: number = 0): React.ReactNode => {
        const isExpanded = expandedNodes.has(node.id);
        const hasChildren = node.children.length > 0;

        const isExplicitlyChecked = selectedCategories.includes(node.id.toString());
        const isImplicitlyChecked = !isExplicitlyChecked && hasAnySelectedDescendant(node);
        const isChecked = isExplicitlyChecked || isImplicitlyChecked;

        const getAllSelectedDescendantIds = (n: CategoryTreeNode): string[] => {
            const ids: string[] = [];
            for (const child of n.children) {
                if (selectedCategories.includes(child.id.toString())) ids.push(child.id.toString());
                ids.push(...getAllSelectedDescendantIds(child));
            }
            return ids;
        };

        return (
            <div key={node.id} className="space-y-1">
                <div className={`flex items-center space-x-2 ${level > 0 ? 'ml-4' : ''}`}>
                    {hasChildren && (
                        <button onClick={() => toggleNode(node.id)} className="rounded p-0.5 transition-colors hover:bg-gray-100">
                            {isExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                            ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                            )}
                        </button>
                    )}
                    {!hasChildren && <div className="w-4" />}

                    <label className="flex flex-1 cursor-pointer items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                                if (!e.target.checked && isImplicitlyChecked) {
                                    // Uncheck all selected descendants instead of the ancestor itself
                                    getAllSelectedDescendantIds(node).forEach((id) => onCategoryChange(id, false));
                                } else {
                                    onCategoryChange(node.id.toString(), e.target.checked);
                                }
                            }}
                            className="rounded border-gray-300"
                        />
                        <span className={`text-sm ${isChecked ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                            {node.name}
                            {node.count !== undefined && <span className="ml-1 font-normal text-gray-500">({node.count})</span>}
                        </span>
                    </label>
                </div>

                {hasChildren && isExpanded && <div className="space-y-1">{node.children.map((child) => renderNode(child, level + 1))}</div>}
            </div>
        );
    };

    if (categoryTree.length === 0) {
        return <p className="text-sm text-gray-500 italic">Nenhuma categoria disponível</p>;
    }

    return <div className="space-y-1">{categoryTree.map((node) => renderNode(node))}</div>;
};
